// The facilitator asking the people who can answer for a confirmation, rendered where it
// was asked.
//
// ---------------------------------------------------------------------------
// THERE IS NO AUTHORIZATION CHECK IN THIS FILE
// ---------------------------------------------------------------------------
//
// Every ask reaches this component already decided: the server refuses to raise one
// anywhere but a principals-only conversation, and serves the list filtered to
// conversations the reader is in. So everybody who is handed one may answer it, and this
// component draws the shape it receives rather than standing in for a check.
//
// Drawing a button for somebody who cannot answer and leaning on the server's 404 is the
// argument for a client-side gate, made by somebody who has forgotten that a button is a
// promise the interface cannot take back.
//
// ---------------------------------------------------------------------------
// AND NO AGE
// ---------------------------------------------------------------------------
//
// Nothing here renders a time on an ask. The facilitator may say things about the state of
// the work and never on somebody's behalf; "waiting three days" beside a name says the
// second one without anybody having written it. `plan/inline.ts` puts an ask with no
// anchor at the foot of the thread rather than borrowing a neighbouring message's clock.
//
// ---------------------------------------------------------------------------
// AN ACTION TYPE THIS BUILD DOES NOT KNOW
// ---------------------------------------------------------------------------
//
// `ACTION_PAYLOAD_ALLOWLIST` names one payload shape per type, and an action of any other
// type arrives with its payload dropped. So an unknown type is an ask whose content this
// client genuinely does not have: it renders as an ask that cannot be shown, with no
// buttons, because a ratify button over an unread payload is a person confirming something
// they were not shown, which is the whole of what human ratification exists to prevent.
//
// ---------------------------------------------------------------------------
// SOMEBODY NAMED HERE WITH NO ADDRESS ON FILE (issue #155, D96)
// ---------------------------------------------------------------------------
//
// A move or an invitation can name a person the roster has no address for yet — D93 moved
// the address read to the moment of approval, so an open one always can. Naming them and
// saying nothing else is the bug this fixes: a bare name here reads exactly like everyone
// else's, and the one thing this control exists to say is what approving actually does.
// So a name with no address gets one more plain sentence, from `unreachableNote` below,
// saying they will not be written to until there is one — before the fact, in the ask
// itself. This row does not also report it AFTER the fact: it used to, from the resolve
// response, but that report was read once, at the moment of approving, because the row it
// appeared on was gone from the next load's list the instant the plan refetches (D70's asks
// are only ever the OPEN ones) — a report that could flash and vanish before it was read.
// The transcript's own account of what happened, the system message `MessageRow.tsx` writes
// when the action resolves, is the one persistent record now, so this row does not keep a
// second, transient one that says the same thing worse.

import { useState } from 'react';

import {
  INVITATION_ACTION,
  INVITATION_DECLINED,
  INVITATION_RATIFIED,
  GRANT_PRINCIPAL_ACTION,
  MOVE_ACTION,
  type PendingAction,
  type Person,
} from '../api/contract.js';
import { nameList } from '../format.js';

export type PendingActionRowProps = {
  action: PendingAction;
  people: readonly Person[];
  /** Resolves the action. See `usePlan.ts`'s `resolve`. */
  resolve: (actionId: string, resolution: string) => Promise<void>;
};

/** The name a reader is allowed to see for a roster entry, or null. */
function nameFor(rosterEntryId: string | null, people: readonly Person[]): string | null {
  if (rosterEntryId === null) {
    return null;
  }
  return people.find((person) => person.id === rosterEntryId)?.name ?? null;
}

/**
 * The address a reader is allowed to see for a roster entry, or null.
 *
 * Read off the person, never off the action's own payload (issue #138): a request that
 * copied an address out of the roster at staging time was a copy a later correction
 * could not reach. Looking it up here, at render time, means a correction to the roster
 * reaches a standing ask with nothing else to redo — the same reason `nameFor` above
 * already looks the name up rather than trusting whatever the payload once carried.
 */
function addressFor(
  rosterEntryId: string | null,
  people: readonly Person[],
): string | null {
  if (rosterEntryId === null) {
    return null;
  }
  return people.find((person) => person.id === rosterEntryId)?.email ?? null;
}

/**
 * The sentence that follows an ask naming people with no address on file, or null when
 * everyone named has one. Plain, and about the people rather than about the request: an
 * address is what this whole confirmation turns on, so silence about a missing one is the
 * bug D96 fixes, not a caveat to soften.
 */
function unreachableNote(
  rosterEntryIds: readonly (string | null)[],
  people: readonly Person[],
): string | null {
  // Somebody already here needs no address and no invitation. A move or an invitation can
  // never name one — the tools refuse it — but a grant of principal status can, and saying
  // a joined person will not be reached would be false.
  const names = rosterEntryIds
    .filter(
      (id) =>
        id !== null &&
        addressFor(id, people) === null &&
        people.find((person) => person.id === id)?.joined !== true,
    )
    .map((id) => nameFor(id, people) ?? 'someone');
  if (names.length === 0) {
    return null;
  }
  // No actor: "Mor doesn't have" invents a person who has things (AGENTS.md). The true
  // sentence is about the address and the person it is missing for.
  return `There’s no address for ${nameList(names)} yet, so this won’t reach them.`;
}

export function PendingActionRow({
  action,
  people,
  resolve,
}: PendingActionRowProps): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const run = async (resolution: string): Promise<void> => {
    setBusy(true);
    setFailed(false);
    try {
      await resolve(action.id, resolution);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  const controls = (yes: string): JSX.Element => (
    <>
      <div className="pending__controls">
        <button
          type="button"
          className="button button--primary"
          disabled={busy}
          onClick={() => void run(INVITATION_RATIFIED)}
        >
          {yes}
        </button>
        <button
          type="button"
          className="button"
          disabled={busy}
          onClick={() => void run(INVITATION_DECLINED)}
        >
          Not yet
        </button>
      </div>
      {failed ? (
        <p className="pending__failed" role="status">
          That didn’t go through. Try again.
        </p>
      ) : null}
    </>
  );

  if (action.type === MOVE_ACTION) {
    // The one confirmation the move needs (D75): the plan changes state, the group
    // conversation opens, and everyone named here gets their invitation, in one act.
    const invitees =
      action.payload !== null && 'invitees' in action.payload
        ? action.payload.invitees
        : [];
    const rosterEntryIds = invitees.map((invitee) => invitee.rosterEntryId);
    const named = rosterEntryIds.map((id) => nameFor(id, people) ?? 'someone');
    const note = unreachableNote(rosterEntryIds, people);
    return (
      <li className="pending">
        <p className="pending__ask">
          {named.length === 0
            ? 'Are you ready to start a group conversation?'
            : `Are you ready to start a group conversation with ${nameList(named)}?`}
        </p>
        {note !== null ? <p className="pending__excluded">{note}</p> : null}
        {controls('Yes, start it')}
      </li>
    );
  }

  if (
    action.type === GRANT_PRINCIPAL_ACTION &&
    action.payload !== null &&
    'rosterEntryId' in action.payload
  ) {
    const person = nameFor(action.payload.rosterEntryId, people) ?? 'this person';
    // Approving promotes an unjoined person and sends them nothing, so this confirmation
    // owes the same warning the other two do (D96). Without it the one request that can
    // leave somebody a principal with no way in is the one that says nothing about it.
    const grantNote = unreachableNote([action.payload.rosterEntryId], people);
    return (
      <li className="pending">
        <p className="pending__ask">Shall Mor make {person} an equal principal?</p>
        {grantNote !== null ? <p className="pending__excluded">{grantNote}</p> : null}
        {controls('Yes, make them a principal')}
      </li>
    );
  }

  if (action.type !== INVITATION_ACTION || action.payload === null) {
    return (
      <li className="pending pending--unknown">
        <p className="pending__ask">
          Mor is waiting on you for something this version can’t show. Ask about it in the
          conversation.
        </p>
      </li>
    );
  }

  const rosterEntryId =
    'rosterEntryId' in action.payload ? action.payload.rosterEntryId : null;
  const name = nameFor(rosterEntryId, people) ?? 'them';
  const email = addressFor(rosterEntryId, people);
  const note = unreachableNote([rosterEntryId], people);

  return (
    <li className="pending">
      <p className="pending__ask">
        Shall Mor write to {name}
        {email === null ? '' : ` at ${email}`}, inviting them in?
      </p>
      {note !== null ? <p className="pending__excluded">{note}</p> : null}
      {controls('Yes, send it')}
    </li>
  );
}
